<script lang="ts">
	import { AnimateSharedLayout, Motion, MotionConfig } from 'svelte-motion'
	import type { Project } from '$types/project'
	import { copyProject, projectSchema } from '$types/project'
	import { t, translate } from '$i18n/i18n'
	import ProjectStatus from '$lib/molecules/ProjectStatus.svelte'
	import InputField from '$lib/molecules/InputField.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import type { FontProps } from '$types/fontProps'
	import { DefaultInputFieldProps } from '$types/fontProps'
	import PrimaryButton from '$lib/atoms/PrimaryButton.svelte'
	import SecondaryButton from '$lib/atoms/SecondaryButton.svelte'
	import { ButtonIcons } from '$types/enums'
	import DatePickerInput from '$lib/atoms/DatePickerInput.svelte'
	import { getContext, hasContext, onMount } from 'svelte'
	import dayjs from 'dayjs'
	import PersonAndCompanyDataAutocomplete from '$lib/autocomplete/PersonAndCompanyDataAutocomplete.svelte'
	import { addNotification } from '$lib/stores'
	import UserAccountAutocomplete from '$lib/autocomplete/UserAccountAutocomplete.svelte'
	import { writable, type Writable } from 'svelte/store'
	import { getFormErrors } from '$utils/formValidation'
	import type { FormError } from '$types/formError'
	import { closeModal, openModal } from 'svelte-modals'
	import Modal from '$lib/organisms/Modal.svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import AddressDataAutocomplete from '$lib/autocomplete/AddressDataAutocomplete.svelte'
	import { currentLot, currentProject, mapFormControls } from '$lib/map/fak/utils/fakStores'
	import type { PersonAndCompanyData } from '$types/personAndCompanyData'
	import {
		copyPersonAndCompanyData,
		DefaultPersonAndCompanyData
	} from '$types/personAndCompanyData'
	import { closeFAK } from '$lib/map/fak/utils/fakUtils'
	import { DefaultLot } from '$lib/map/types/lot'
	import {
		hideAutosaveInfo,
		showAutosaveCheckmark,
		showAutosaveCrossmark,
		showAutosaveLoading
	} from '$utils/autosaveInfoUtils'
	import {
		copyUserAccountData,
		DefaultUserAccountData,
		type UserAccountData
	} from '$types/userAccountData'
	import { jwt } from '$utils/jwt'
	import { NotificationType } from '$types/notification'

	export let resizeObserver: ResizeObserver | null = null

	/*font properties for all the input fields */
	const fontProps: FontProps = DefaultInputFieldProps()
	const PROJECT_FORM_ERROR_PATH_PREFIX = 'project.'
	const PROJECT_AUTOSAVE_TIMEOUT = 2000

	let formErrors: Writable<FormError[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])

	let loginUser: UserAccountData = DefaultUserAccountData()
	if ($jwt) {
		jwt.subscribe((claims: Record<string, string>) => {
			loginUser.id = Number(claims.uid)
			loginUser.firstName = claims.ufirstname
			loginUser.lastName = claims.ulastname
		})
	}

	$: isNew = $currentProject!.id === 'new'

	let startDate: number | null
	let endDate: number | null

	/* Track the last project dates we synced from the store so we only update
	the date pickers when the stored value actually changes (e.g. after a lot
	save expands the project end date), without interfering with user edits. */
	let _syncedProjectStartDate: string | null = null
	let _syncedProjectEndDate: string | null = null

	$: {
		const projectStartDate = $currentProject?.startDate ?? null
		const projectEndDate = $currentProject?.endDate ?? null

		if (projectStartDate !== _syncedProjectStartDate) {
			//assignment is used reactively for the if condition:
			// eslint-disable-next-line no-useless-assignment
			_syncedProjectStartDate = projectStartDate
			startDate = projectStartDate ? dayjs(projectStartDate, 'YYYY-MM-DD').unix() : null
		}
		if (projectEndDate !== _syncedProjectEndDate) {
			//assignment is used reactively for the if condition:
			// eslint-disable-next-line no-useless-assignment
			_syncedProjectEndDate = projectEndDate
			endDate = projectEndDate ? dayjs(projectEndDate, 'YYYY-MM-DD').unix() : null
		}
	}

	let rerenderTrigger: Record<string, never> | null = null

	let contactPerson: PersonAndCompanyData | null = null
	let personLink: string | null = null
	let onboardingComment: string | null = null

	initializeFieldsAndObjects()

	function initializeFieldsAndObjects() {
		// read the id directly — the reactive `isNew` is still stale (true) when this
		// runs synchronously right after a save sets `$currentProject` to the saved
		// project, which would clobber the just-saved case owner with the login user.
		if ($currentProject!.id === 'new') {
			$currentProject!.caseOwner = copyUserAccountData(loginUser)
		}
		startDate = $currentProject!.startDate
			? dayjs($currentProject!.startDate, 'YYYY-MM-DD').unix()
			: null
		endDate = $currentProject!.endDate ? dayjs($currentProject!.endDate, 'YYYY-MM-DD').unix() : null
		rerenderTrigger = {}
		contactPerson = $currentProject?.contactPersonAndCompany ?? null
		personLink = $currentProject?.contactPersonAndCompany?.link ?? null
		onboardingComment = $currentProject?.contactPersonAndCompany?.onboardingComment ?? null
	}

	addEventListener('saveProject', autosaveProjectHandler)

	let timeout: ReturnType<typeof setTimeout>

	/* Starts the autosave process where the actual save function is called only after a specified timeout when no more
    changes are made. */
	function autosaveProjectHandler() {
		if (!contactPerson) {
			$currentProject!.contactPersonAndCompany = DefaultPersonAndCompanyData()
		} else {
			$currentProject!.contactPersonAndCompany =
				copyPersonAndCompanyData(contactPerson) ?? DefaultPersonAndCompanyData()
		}
		$currentProject!.contactPersonAndCompany.link = personLink ?? undefined
		$currentProject!.contactPersonAndCompany.onboardingComment = onboardingComment ?? undefined

		if ($currentProject && $currentProject!.id !== 'new') {
			$mapFormControls.saving = true
			showAutosaveLoading()
			clearTimeout(timeout)
			timeout = setTimeout(
				() => validateAndSaveProject(copyProject($currentProject!)),
				PROJECT_AUTOSAVE_TIMEOUT
			)
		}
	}

	async function validateAndSaveProject(projectToSave: Project) {
		projectToSave.startDate = startDate ? dayjs.unix(startDate).add(12, 'h').toISOString() : ''
		projectToSave.endDate = endDate ? dayjs.unix(endDate).add(12, 'h').toISOString() : ''

		$formErrors = [{ path: '', message: '' }]

		await projectSchema
			.validate(projectToSave, {
				stripUnknown: true,
				abortEarly: false
			}) // stripUnknown strips fields that are not stated in the validator
			.then(async (validatedProject) => {
				await fetchUtils
					.post(`/api/project/${validatedProject.id}/update`, validatedProject, false)
					.then((data) => {
						$currentProject = copyProject(data)
						initializeFieldsAndObjects()
						if (validatedProject.id === 'new') {
							let notification = {
								message: translate('de', 'UI.map.project.new.success', []),
								type: NotificationType.SUCCESS,
								dismissible: true,
								timeout: 5000
							}
							addNotification(notification)
							$currentLot = DefaultLot()
						} else {
							showAutosaveCheckmark('(Projekt ' + $currentProject!.code + ')')
						}
					})
					.catch((e) => {
						fetchUtils.catchErrorAndShowNotification()(e)
						hideAutosaveInfo()
						if (e.data) {
							$currentProject = copyProject(e.data)
							initializeFieldsAndObjects()
						}
					})
			})
			.catch((error) => {
				$formErrors = getFormErrors(error, PROJECT_FORM_ERROR_PATH_PREFIX)
				showAutosaveCrossmark('Projekt ' + $currentProject!.code)
			})
		$mapFormControls.saving = false
	}

	function cancel() {
		openModal(Modal, {
			title: $t('UI.mapForm.close.modal.title'),
			denyText: $t('UI.mapForm.close.modal.confirm'),
			message: $t('UI.mapForm.close.modal.desc'),
			acceptIcon: ButtonIcons.NONE,
			denyIcon: ButtonIcons.NONE,
			onDeny: () => {
				closeModal()
				closeFAK()
			}
		})
	}

	onMount(() => {
		if (resizeObserver) {
			const el = document.getElementById('project-form-container')
			if (el) resizeObserver.observe(el)
		}
	})
</script>

<MotionConfig
	transition={{ duration: 0.6, delay: 0.1, type: 'spring', stiffness: 80, damping: 9, mass: 0.55 }}
>
	<!--type: tween is also very smooth and does
not have the bouncing at the end for the address search box-->
	<AnimateSharedLayout>
		<Motion let:motion>
			<div class="project-form-container" id="project-form-container" use:motion>
				{#if $currentProject}
					<div class="project-main-data">
						{#if isNew}
							<span>{$t('UI.map.project.create.header')}</span>
						{:else}
							<ProjectStatus {...$currentProject} />
						{/if}
						<div>
							<div style="grid-area: projectName">
								<InputField
									bind:value={$currentProject.projectName}
									id="project-name"
									format={inputFieldFormats.FULL}
									placeholder={$t('UI.map.project.name.ph')}
									{fontProps}
									readOnly={!$currentProject.isEditable}
									errorPath={PROJECT_FORM_ERROR_PATH_PREFIX + 'projectName'}
									required
									on:focusout={autosaveProjectHandler}
								/>
							</div>
							<div style="grid-area: caseOwner">
								{#key rerenderTrigger}
									<UserAccountAutocomplete
										bind:account={$currentProject.caseOwner}
										disabled={!$currentProject.isEditable}
										id="project-case-owner"
										{fontProps}
										label={$t('UI.map.project.caseOwner.ph')}
										errorPath={PROJECT_FORM_ERROR_PATH_PREFIX + 'caseOwner.*'}
										required
										onChange={autosaveProjectHandler}
									/>
								{/key}
							</div>
							<div style="grid-area: contactPerson">
								<PersonAndCompanyDataAutocomplete
									bind:contactPerson
									bind:onboardingComment
									bind:personLink
									isNew={$currentProject.id === 'new'}
									personInputId="project-contact-person"
									{fontProps}
									company={$currentProject.contactPersonAndCompany}
									outerId={$currentProject.id}
									errorPath={PROJECT_FORM_ERROR_PATH_PREFIX + 'contactPerson'}
									isEditable={$currentProject.isEditable}
									required
									onChange={autosaveProjectHandler}
								/>
								<!-- no need to append '.*' to path here, is already done in component itself + we need the path for the separated subfields -->
							</div>
						</div>
					</div>
					<div class="project-data-row">
						<span>{$t('UI.map.timeframe')}</span>
						<div>
							<DatePickerInput
								bind:date={startDate}
								id="project-select-start-date"
								{fontProps}
								label=""
								format={inputFieldFormats.FULL}
								placeholder={$t('UI.startDate.label')}
								disabled={!$currentProject.isEditable}
								errorPath={PROJECT_FORM_ERROR_PATH_PREFIX + 'startDate'}
								onchange={autosaveProjectHandler}
							/>
							<DatePickerInput
								bind:date={endDate}
								id="project-select-end-date"
								{fontProps}
								label=""
								format={inputFieldFormats.FULL}
								placeholder={$t('UI.endDate.label')}
								disabled={!$currentProject.isEditable}
								errorPath={PROJECT_FORM_ERROR_PATH_PREFIX + 'endDate'}
								onchange={autosaveProjectHandler}
							/>
						</div>
					</div>
					<div class="project-data-row" style="row-gap: 0.25rem">
						<AddressDataAutocomplete
							bind:addressData={$currentProject.address}
							{fontProps}
							outerId={$currentProject.id}
							isEditable={$currentProject.isEditable}
							errorPath={PROJECT_FORM_ERROR_PATH_PREFIX + 'addressData'}
							label={$t('UI.map.executionPlace')}
							idPrefix="project"
							required
							onChange={autosaveProjectHandler}
						/>
						<!-- no need to append '.*' to path here, is already done in component itself + we need the path for the separated subfields -->
					</div>
					{#if isNew}
						<div class="new-project-buttons">
							<SecondaryButton
								label={$t('UI.button.cancel')}
								paddingHorizontal="1.375rem"
								paddingTop="0.875rem"
								sizeAdaptingToText
								on:click={cancel}
							/>

							<PrimaryButton
								id="create-project-button"
								label={$t('UI.map.project.form.createProject')}
								leftIcon={ButtonIcons.PLUS_BOLD}
								iconSize="24px"
								paddingHorizontal="1.375rem"
								paddingTop="0.875rem"
								sizeAdaptingToText
								whiteIcon
								on:click={() => {
									showAutosaveLoading()
									if ($currentProject) validateAndSaveProject(copyProject($currentProject))
								}}
							/>
						</div>
					{/if}
				{/if}
			</div>
		</Motion>
	</AnimateSharedLayout>
</MotionConfig>

<style lang="scss">
	@import '$styles/global';
	@import '$styles/autocomplete';

	.project-form-container {
		background-color: white;
		border-radius: 0.25rem;
		padding: 1.625rem 1.25rem 1.625rem;
		width: 450px;
		max-width: 450px;

		@include flex-col(1.25rem, $justify: flex-start);

		position: relative; /* necessary for the close button */
	}

	.project-main-data {
		width: 100%;

		@include flex-col(1.25rem, $alignment: flex-start);

		& > span:first-child {
			@include roboto-font(19px, 400, 1rem, black);
		}

		& > div:not(:first-child) {
			width: 100%;
			display: grid;
			grid-template-columns: 1fr 1fr;
			grid-template-areas:
				'projectName caseOwner'
				'contactPerson contactPerson';
			gap: 0.938rem 0.625rem;
		}
	}

	.project-data-row {
		width: 100%;

		@include flex-col(0.75rem, $alignment: flex-start);

		& > span,
		& > span > span {
			@include roboto-font(1rem, 400, 0.875rem, black);
		}

		& > div,
		& > div > div {
			width: 100%;

			@include flex-row(0.625rem, $justify: flex-start, $alignment: flex-start);
		}
	}

	.cancel-btn {
		background-color: $grey-700;
		border-radius: 50%;
		width: 32px;
		height: 32px;
		position: absolute;
		top: -16px; /* half of the element above top of container */
		right: -16px; /* half of the element to the right of container */

		@include flex-col;

		&:focus {
			cursor: pointer;
		}
	}

	.invisible {
		opacity: 0;
	}

	.new-project-buttons {
		@include flex-row(0.375rem, center, flex-end);

		width: 100%;
		margin-top: -0.375rem;
	}

	.duplicate-address-icon {
		background-color: white;
		background-size: contain;
		position: relative;
		display: inline-block;
		text-align: center;
		width: 24px;
		height: 24px;
		margin-right: 6px;

		&:hover {
			cursor: pointer;
		}
	}
</style>
